import React, { useState, useEffect } from "react";
import { TextField, Button, InputAdornment, IconButton } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import EyeIcon from "@styles/all-images/icons/eye-icon.svg";
import EyeOffIcon from "@styles/all-images/icons/eye-off-icon.svg";
import actionsHandler from "@/store/actions/actionHandler";
import { RootState, AppDispatch } from "@/store";
import { clearTempData, setLoading } from "@/store/slices/authSlice";

const { unAuthenticatedCall: resetPassword } = actionsHandler("resetPassword");

const ResetPassword = () => {
  const { t } = useTranslation("auth");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { tempEmail, loading, forgotPasswordToken } = useSelector(
    (state: RootState) => state.auth,
  );

  const validationSchema = Yup.object({
    new_password: Yup.string()
      .required(t("resetPassword.form.newPassword.validation.required"))
      .min(8, t("resetPassword.form.newPassword.validation.min"))
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
        t("resetPassword.form.newPassword.validation.pattern"),
      ),
    confirm_password: Yup.string()
      .required(t("resetPassword.form.confirmPassword.validation.required"))
      .oneOf(
        [Yup.ref("new_password")],
        t("resetPassword.form.confirmPassword.validation.match"),
      ),
  });

  const formik = useFormik({
    initialValues: {
      new_password: "",
      confirm_password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        dispatch(setLoading(true));
        const response = await dispatch(
          resetPassword({
            payload: {
              email: tempEmail,
              new_password: values.new_password,
              confirm_password: values.confirm_password,
              user_token: forgotPasswordToken,
            },
            method: "post",
          }),
        );

        if (response.ok) {
          toast.success(
            response.data.message || t("resetPassword.messages.success"),
          );
          navigate("/");
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error(t("resetPassword.messages.unexpectedError"));
      } finally {
        dispatch(setLoading(false));
      }
    },
  });

  useEffect(() => {
    if (!tempEmail) {
      navigate("/forget-password");
    }
  }, [tempEmail, navigate]);

  return (
    <div className="tw-min-h-screen tw-flex">
      {/* Left Section */}
      <div className="lg:tw-w-3/4 md:tw-w-2/4 sm:tw-w-full tw-hidden login-bg tw-bg-cover tw-bg-center sm:tw-flex tw-items-end tw-justify-start tw-text-white tw-p-14">
        <div className="tw-px-4">
          <h1 className="tw-font-bold font-size-5xl tw-mb-4 tw-leading-[74px]">
            Innovative Collateral & <br /> Risk Solutions
          </h1>
        </div>
      </div>

      {/* Right Section */}
      <div className="lg:tw-w-1/3 md:tw-w-2/4 sm:tw-w-full tw-w-full tw-flex tw-items-center tw-justify-center">
        <div className="tw-w-full tw-max-w-md tw-px-16">
          <div className="tw-mb-8">
            <img
              src="/logo/Logo.svg"
              alt="Logo"
              className="tw-mx-auto tw-max-w-[150px] tw-mb-14"
            />
            <h5 className="tw-text-[22px] font-primary font-primary-color font-weight-semibold tw-leading-6">
              {t("resetPassword.title")}
            </h5>
            <p className="tw-mt-2 tw-leading-6 font-size-sm font-gray-color font-weight-medium">
              {t("resetPassword.subtitle")}
            </p>
          </div>

          <form onSubmit={formik.handleSubmit}>
            {/* New Password Input */}
            <div className="form-group tw-mt-6">
              <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                {t("resetPassword.form.newPassword.label")}
              </p>
              <TextField
                fullWidth
                name="new_password"
                type={showPassword ? "text" : "password"}
                variant="outlined"
                placeholder={t("resetPassword.form.newPassword.placeholder")}
                value={formik.values.new_password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.new_password &&
                  Boolean(formik.errors.new_password)
                }
                className="tw-mb-4"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        className="tw-p-1 tw-border tw-border-gray-300 tw-rounded-full hover:tw-bg-gray-100"
                      >
                        <img
                          src={showPassword ? EyeIcon : EyeOffIcon}
                          alt={showPassword ? "Hide Password" : "Show Password"}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <p className="tw-text-xs tw-text-red-500 tw-mt-1">
                {formik.touched.new_password && formik.errors.new_password}
              </p>
            </div>

            {/* Confirm Password Input */}
            <div className="form-group tw-mt-6 tw-mb-6">
              <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                {t("resetPassword.form.confirmPassword.label")}
              </p>
              <TextField
                fullWidth
                name="confirm_password"
                type={showConfirmPassword ? "text" : "password"}
                variant="outlined"
                placeholder={t(
                  "resetPassword.form.confirmPassword.placeholder",
                )}
                value={formik.values.confirm_password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.confirm_password &&
                  Boolean(formik.errors.confirm_password)
                }
                className="tw-mb-4"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                        className="tw-p-1 tw-border tw-border-gray-300 tw-rounded-full hover:tw-bg-gray-100"
                      >
                        <img
                          src={showConfirmPassword ? EyeIcon : EyeOffIcon}
                          alt={
                            showConfirmPassword
                              ? "Hide Password"
                              : "Show Password"
                          }
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <p className="tw-text-xs tw-text-red-500 tw-mt-1">
                {formik.touched.confirm_password &&
                  formik.errors.confirm_password}
              </p>
            </div>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              className="tw-py-2 primary-custom-button"
            >
              {loading
                ? t("resetPassword.form.button.loading")
                : t("resetPassword.form.button.submit")}
            </Button>
            <div className="tw-text-center tw-mt-6">
              <a
                href="/"
                className="tw-text-sm tw-font-medium tw-leading-6 font-gray-color"
              >
                {t("signIn.backToLogin")}
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
